import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';

import { DxcScrollHintDirective } from '../scroll-hint/dxc-scroll-hint.directive';

/** Una pestaña: su clave, su rótulo y, si la tiene, la cifra de lo que hay dentro. */
export type DxcTab<K extends string = string> = { key: K; label: string; count?: number | string | null };

const COUNT = new Intl.NumberFormat('es-MX');

/**
 * Pestañas del catálogo (`.dxc-tabs`) con su comportamiento: el carril, la
 * pestaña elegida en `aria-selected` y el teclado de un `tablist` —flechas,
 * Inicio y Fin, con el foco siguiendo a la selección (§9)—.
 *
 * Existe porque las clases solas dejaban el teclado a cada vista: el
 * complemento de pago las compone a mano sin flechas, y el dashboard las
 * resolvió en su componente — la segunda variante del dashboard iba a copiar
 * ese mismo método. Las clases siguen siendo válidas para una pestaña que es
 * una ruta (`<a class="dxc-tab" aria-current="page">`); esto es para vistas de
 * una misma pantalla.
 *
 * La cifra de cada pestaña dice cuánto hay en las que no se ven.
 *
 * Por debajo del marco (`lg`) el carril avisa de que hay más —`dxcScrollHint`
 * pone las clases y la hoja pinta el desvanecido de canto— y, con
 * `dxcScrollHintReveal`, lleva la pestaña elegida a la vista: a 390 la de
 * Ajustes › Correos quedaba fuera del carril y nada decía cuál estaba elegida. En escritorio ni lo uno ni lo
 * otro: el árbol y el desplazamiento son los de siempre.
 *
 * @example
 * <dxc-tabs [tabs]="tabs" [(value)]="tab" ariaLabel="Detalle del periodo"></dxc-tabs>
 */
@Component({
    standalone: true,
    selector: 'dxc-tabs',
    template: `
        <nav class="dxc-tabs" role="tablist" [attr.aria-label]="ariaLabel || null" (keydown)="onKey($event)"
            dxcScrollHint dxcScrollHintBelow="lg" dxcScrollHintReveal='[aria-selected="true"]'>
            @for (tab of tabs; track tab.key) {
                <button type="button" class="dxc-tab" role="tab"
                    [attr.aria-selected]="tab.key === value"
                    [attr.tabindex]="tab.key === value ? 0 : -1"
                    (click)="select(tab.key)">
                    {{ tab.label }}
                    @if (tab.count !== undefined && tab.count !== null) {
                        <span class="dxc-tab__count">{{ format(tab.count) }}</span>
                    }
                </button>
            }
        </nav>
    `,
    host: { class: 'dxc-tabs-host' },
    imports: [DxcScrollHintDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcTabsComponent<K extends string = string> {

    @Input() tabs: DxcTab<K>[] = [];
    @Input() value!: K;
    @Input() ariaLabel = '';

    @Output() valueChange = new EventEmitter<K>();

    constructor(private host: ElementRef<HTMLElement>) {}

    select(key: K): void {
        if (key === this.value) return;
        this.value = key;
        this.valueChange.emit(key);
    }

    format(count: number | string): string {
        return typeof count === 'number' ? COUNT.format(count) : count;
    }

    onKey(event: KeyboardEvent): void {
        if (!this.tabs.length) return;

        const current = Math.max(0, this.tabs.findIndex(tab => tab.key === this.value));
        let next: number;
        if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else return;

        event.preventDefault();
        this.select(this.tabs[next].key);
        this.host.nativeElement.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
    }
}
